import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { useBrickStore } from '@store/brickStore'
import { globalProfiler, isProfilingEnabled } from '@utils/performanceProfiling'
import { isPhysicsDisabled } from '@utils/debug'

/**
 * FPSMonitor Component
 *
 * Canvas-level component that monitors FPS using useFrame.
 * Must be rendered inside Canvas component (R3F context).
 *
 * Calculates FPS by tracking frame delta times over a rolling window
 * and updates via callback for external display.
 *
 * Task 4.1.0 - Debug mode FPS monitoring
 * Task 4.1.6 - Performance profiling integration
 *
 * @component
 */
export const FPSMonitor: React.FC<{ onFPSUpdate: (fps: number) => void }> = ({ onFPSUpdate }) => {
  const frameTimesRef = useRef<number[]>([])
  const updateCounterRef = useRef(0)
  const bricks = useBrickStore((state) => state.bricks)
  const profilingEnabled = isProfilingEnabled()
  const physicsDisabled = isPhysicsDisabled()

  useFrame((_state, delta) => {
    // Track frame times (last 60 frames for smooth averaging)
    frameTimesRef.current.push(delta)
    if (frameTimesRef.current.length > 60) {
      frameTimesRef.current.shift()
    }

    // Update FPS display every 10 frames (avoid excessive updates)
    updateCounterRef.current++
    if (updateCounterRef.current >= 10) {
      updateCounterRef.current = 0

      if (frameTimesRef.current.length > 0) {
        const avgDelta = frameTimesRef.current.reduce((a, b) => a + b, 0) / frameTimesRef.current.length
        const fps = Math.round(1 / avgDelta)
        const cappedFPS = Math.min(fps, 144) // Cap at 144 for display (reasonable max)
        onFPSUpdate(cappedFPS)

        // Task 4.1.6: Record frame metrics for performance profiling
        if (profilingEnabled && globalProfiler.isProfilingActive()) {
          globalProfiler.recordFrame(cappedFPS, bricks.size)
        }
      }
    }
  })

  // Task 4.1.6: Auto-start profiling if ?profile=true is set
  useRef(() => {
    if (profilingEnabled && !globalProfiler.isProfilingActive()) {
      globalProfiler.startProfiling(!physicsDisabled)
      console.log('[FPSMonitor] Auto-started profiling')
    }
  })

  return null
}
